
import 'custom-event-polyfill';

// spinnerLocation will store a reference to our spinner's GIF file
let spinnerLocation;
const xhrResponseCache = {};


const isWidgetViewer = /\?widgets=[\w-]+@\d+\.\d+\.\d+&loaderVersion=\d+\.\d+\.\d+(?:&host)?/.test(window.location.href);

/**
 * Return reference to cached widget
 *
 * @param {*} widgetName name of widget
 * @param {*} widgetVersion version of widget
 * @returns {String} name-version
 */
function getCacheKey(widgetName, widgetVersion) {
  return `${widgetName}-${widgetVersion}`;
}

/**
 * Function to determine if page is running on localhost
 *
 * @returns {Boolean} true if the widget is running on localhost
 */
function isLocal() {
  return window.location.href.indexOf('localhost') > -1;
}

/**
  * Create script element and append it to the head
  *
  * @param {object<Element>} _script Script element
  * @param {object} options Function options
  * @returns {object<Element>} Script element
  */
function createScript(_script, options = {}) {
  const script = document.createElement('script');

  const { skipBabelPolyFill } = options;

  // Copy the following properties
  const props = [
    'async',
    'charset',
    'crossorigin',
    'integrity',
    'language',
    'nonce',
    'onload',
    'referrerpolicy',
    'src',
    'type',
  ];

  props.forEach((prop) => {
    const propVal = _script.getAttribute(prop);
    if (prop === 'src') {
      if (propVal) {
        // skip babel-polyfill loading when specified in config
        if (skipBabelPolyFill && (script.src.indexOf('babel-polyfill.js') !== -1 || propVal.indexOf('babel-polyfill.js') !== -1)) return;

        script[prop] = `${propVal}?_=${Date.now()}`;
        return;
      }
      script.innerHTML = _script.innerHTML.replace(
        '.config({',
        '.config({urlArgs: "_=" + Date.now(),',
      );
    }

    if (!propVal) return;

    script[prop] = propVal;
  });

  // Append the script to the head
  document.getElementsByTagName('head')[0].appendChild(script);

  return script;
}

/**
  * IE11 and older script loader
  *
  * @param {object<Array>} scripts Collection of script elements
  * @param {object} options Function options
  * @returns {*} false if no collection, self if async
  */
function IELegacyScriptLoader(scripts, options) {
  if (!Array.isArray(scripts)) return false;
  if (scripts.length === 0) return false;
  const jscripts = [].slice.call(scripts || []);
  let s = jscripts.pop();
  s = createScript(s, options);

  // Ignore async modules it is up to the developers to properly handle it
  if (s.onload) return IELegacyScriptLoader(jscripts, options);

  s.onload = () => {
    IELegacyScriptLoader(jscripts, options);
  };
}

/**
 * To get Widget Loader Spinnner path.
 * @returns {string} Widget Loader spinner path.
 */
function getWidgetLoaderSpinnerPath() {
  const scripts = [].slice.call(document.getElementsByTagName('script') || []);
  const widgetLoaderIndexRegex = '(http|https)://(.)*/spa/widgets/loader/([0-9]+).([0-9]+).([0-9]+)/index.js';
  const widgetLoaderScripts = scripts.reverse()
    .filter(script => script.src && script.src.match(widgetLoaderIndexRegex));

  if (widgetLoaderScripts && widgetLoaderScripts.length) {
    const currentScriptPath = widgetLoaderScripts[0].src.split('/');
    currentScriptPath[currentScriptPath.length - 1] = 'loading.gif';
    return currentScriptPath.join('/');
  }
  console.error('Widget loader script does not exist');
  return false;
}

/**
 * To get cached widget response.
 * @param {string} name Widget name.
 * @param {string} version Widget version.
 * @returns {string} Cached Widget response.
 */
function getCachedWidget(name, version) {
  const cacheKey = getCacheKey(name, version);
  return xhrResponseCache[cacheKey];
}

/**
 * To remove widget response from cache.
 * @param {string} name Widget name.
 * @param {string} version Widget version.
 * @returns {boolean} True/False for deletion of an object from cache.
 */
function cleanCachedWidget(name, version) {
  const cacheKey = getCacheKey(name, version);
  return delete xhrResponseCache[cacheKey];
}

/**
 * @method getDispatcherElement
 * @description checks to see if DispatcherElement exists and creates it if necessary
 * @returns {Node} dispatcher element that was generated
 */
function getDispatcherElement() {
  /**
   * @const dispatcherDataAttribute
   * @description data attribute that our DispatcherElement will have
   */
  const dispatcherDataAttribute = 'sparta-event-dispatcher';

  /**
   * @const dispatcherElementSelector
   * @description selector that will be used to determine if our DispatcherElement already exists
   */
  const dispatcherElementSelector = `[data-${dispatcherDataAttribute}]`;

  /**
   * @const element
   * @description selected DispatcherElement from document
   */
  const element = document.querySelector(dispatcherElementSelector);

  // if DispatcherElement does not yet exist
  if (!element) {
    /**
     * @const generatedElement
     * @description created element to become our DispatcherElement
     */
    const generatedElement = document.createElement('div');

    // set our DispatcherElement data attribute
    generatedElement.setAttribute(`data-${dispatcherDataAttribute}`, true);
    spinnerLocation = getWidgetLoaderSpinnerPath();

    return generatedElement;
  }

  // return selected DispatcherElement element
  return element;
}

/**
 * @method getCallingScript
 * @description grabs the currently executing script and returns the src
 * @returns {String} current executing src
 */
function getCallingScript() {
  if (document.currentScript) return document.currentScript;
  const scripts = document.getElementsByTagName('script');
  return scripts[scripts.length - 1];
}

const EVENTS = {
  ERROR_LOADING_WIDGET: 'errorLoadingWidget',
  REFRESH_WIDGET: 'refreshWidget',
};

/**
 * @class WidgetLoader
 * @description class for our WidgetLoader that will initialize widgets and facilitate communication
 * between widget and page
 */
class WidgetLoaderClass {
  /**
   * @method constructor
   * @description initializes a new instance of WidgetLoader and makes sure our DispatcherElement
   * exists
   */
  constructor() {
    this.dispatcherElement = getDispatcherElement();
  }

  /**
   * Gets the namespace of the widget
   *
   * @returns {String} widget's namespace
   */
  getNamespace() {
    return getCallingScript().dataset.widgetName;
  }

  /**
   * @method pub
   * @description creates and dispatches a native CustomEvent on our DispatcherElement
   * @param {String} eventName the event name to dispatch
   * @param {*} [detail={}] the event data to attach to the event.detail parameter
   * @returns {void}
   */
  pub(eventName, detail = {}) {
    /**
     * @const evt
     * @description native CustomEvent to dispatch
     */
    const evt = new CustomEvent(eventName, { detail });

    // dispatch our CustomEvent from our DispatcherElement
    this.dispatcherElement.dispatchEvent(evt);
  }

  /**
   * @method sub
   * @description attaches an event listener to our DispatcherElement
   * @param {String} eventName the event name to listen for
   * @param {Function} callback the event handler to fire
   * @returns {void}
   */
  sub(eventName, callback) {
    // add event listener to DispatcherElement
    this.dispatcherElement.addEventListener(eventName, callback);
  }

  /**
   * @method unsub
   * @description allows subscriber to unsubscribe from event
   * @param {String} eventName the event name to listen for
   * @param {Function} callback the event handler to fire
   * @returns {void}
   */
  unsub(eventName, callback) {
    this.dispatcherElement.removeEventListener(eventName, callback);
  }

  /**
   * @method once
   * @description allows subscriber to listen to one instance of event and then unsub
   * @param {String} eventName the event name to listen for
   * @param {Function} callback the event handler to fire
   * @returns {void}
   */
  once(eventName, callback) {
    const fn = (e) => {
      this.dispatcherElement.removeEventListener(eventName, fn);

      callback(e);
    };

    this.dispatcherElement.addEventListener(eventName, fn);
  }

  /**
   * @method canRefreshWidget
   * @description To determine if we can refresh Widgets.
   * @param {string} widgetName Widget name.
   * @param {string} widgetVersion Widget version.
   * @param {boolean} enableWidgetRefresh Optional Flag to enable/disable widget refresh.
   * Default value is true.
   * @returns {boolean} Return true if we can refresh widgets.
   */
  canRefreshWidget(widgetName, widgetVersion, enableWidgetRefresh) {
    // refresh widget if it exists, otherwise load it
    const widgets = [].slice
      .call(document.querySelectorAll(`[data-sparta-container="${widgetName}"][data-version="${widgetVersion}"]`) || []);

    return enableWidgetRefresh && widgets.length > 0;
  }

  /**
   * @method load
   * @description loads a widget into the specified container
   * @param {Object} Config object, must contain keys 'name, version, container, path',
   * optional spinner Boolean value
   * @returns {void}
   */
  load({
    name, version, container, path, spinner, options = {}, language = 'en', enableWidgetRefresh = true, enableWidgetCache = false,
  }) {
    // remove cache if enableWidgetCache is false.
    if (!enableWidgetCache) {
      cleanCachedWidget(name, version);
    }

    if (!name || !version || !container) {
      console.error('invalid config object passed to widget loader');
      return false;
    }

    let containersById = [];
    const containerNodes = [];

    // check if loading widget in multiple places
    if (Object.prototype.toString.call(container) === '[object Array]') {
      window.sparta.widgetLoader[name] = { [version]: {} };
      containersById = container;
    } else {
      containersById.push(container);
    }

    // loop over the containers, check if they exist and load as appropriate
    containersById.forEach((currentContainer) => {
      // window.sparta.widgetLoader[name][version][currentContainer] = {loaded:false};

      // get Node for widget container
      const dest = document.getElementById(currentContainer);

      // if widget container does not exist
      if (!dest) {
        console.error('widget container destination does not exist');
        return false;
      }

      containerNodes.push(dest);
      return true;
    });

    if (this.canRefreshWidget(name, version, enableWidgetRefresh)) {
      this.pub(EVENTS.REFRESH_WIDGET, { name, version, container: containerNodes });
    } else {
      if (!getCachedWidget(name, version)) {
        this.generateStyleSheet(containerNodes, spinner);
      }
      this.widgetXHR(containerNodes, path, name, version, options, language);
    }
    return true;
  }

  /**
   * @method generatedStyleSheet
   * @description add stylesheet for loading states so we don't need another HTTP request
   * @param {Array} containers array of widget container Nodes
   * @param {Boolean} [spinner=true] should we use default spinner
   * @returns {void}
   */
  generateStyleSheet(containers, spinner = true) {
    containers.forEach((dest) => {
      // generate stylesheet for loading
      const style = document.createElement('style');

      let styles = `
        #${dest.id} > [data-sparta-container].sparta-widget-loading,
        #${dest.id} > [data-sparta-container].sparta-widget-loading > .sparta-widget {
          visibility: hidden;
          height: 100%;
          width: 100%;
        }
      `;

      // if we are supposed to show our spinner, add a style to the widget container
      if (spinner) {
        styles += `
          #${dest.id}.sparta-widget-loading {
            height: 100%;
            background-image: url('${spinnerLocation}');
            background-repeat: no-repeat;
            background-position: center center;
          }
        `;
      }

      // add stylesheet to page
      style.type = 'text/css';
      style.id = `${dest.id}-sparta-widget-style-sheet`;

      if (style.stylesSheet) {
        style.styleSheet.cssText = styles;
      } else {
        style.appendChild(document.createTextNode(styles));
      }

      document.head.appendChild(style);

      dest.classList.add('sparta-widget-loading');
    });
  }

  /**
   * @method widgetXHR
   * @description loads widget via AJAX request
   * @param {Array} containers array of widget container Nodes
   * @param {String} path where to load the widget from
   * @param {String} name the name of the widget
   * @param {String} version the version number of the widget to load
   * @param {Object} options sparta options
   * @param {String} language the language to attempt to load (en default)
   * @returns {void}
   */
  widgetXHR(containers, path, name, version, options, language) {
    /**
     * xhrFunction
     *
     * @param {*} responseText response from xhr request
     * @param {Boolean} wasCached is this a cached widget
     * @returns {void}
     */
    function xhrFunc(responseText, wasCached) {
      containers.forEach((currentContainer) => {
        currentContainer.innerHTML = responseText;
      });

      // grab the last item and load the JS into that
      const dest = containers[containers.length - 1];
      dest.style.height = 'auto';
      dest.style.backgroundImage = '';

      // get all script tags so we can make the scripts load
      const myScripts = [].slice.call(dest.querySelectorAll('script') || []);

      const removeScriptsFromDom =
        scripts => scripts.forEach(script => script.parentNode.removeChild(script));
      dest.querySelector('[data-sparta-widget]').setAttribute('data-sparta-options', JSON.stringify(options));

      if (wasCached) {
        // Since Scripts are added to <head> during first run, we need not to keep it during reload.

        const initArray = [];
        const moduleArray = [].slice.call(dest.querySelectorAll('[data-component="module"]') || []);
        moduleArray.forEach((element) => {
          if (element.dataset.module.indexOf('module') !== -1) {
            const { dataset } = element;
            initArray.push(window.sparta.require[name][version].require(`modules/${dataset.moduleRef}/${dataset.version}/js/${dataset.module}`));
          }
        });

        const bootstrap = window.sparta.require[name][version].require('global/sparta-bootstrap-utility');
        const widget = window.sparta.require[name][version].require(`modules/${name}/${version}/js/${name}`);
        initArray.push(widget);
        bootstrap.default.init(initArray, { name, version });
      } else {
        // iterate through scripts
        if (window.navigator.userAgent.match(/(MSIE|Trident)/)) {
          IELegacyScriptLoader(myScripts.reverse(), options);
        } else {
          myScripts.forEach(scriptInput => createScript(scriptInput, options));
        }
        // Remove all scripts from Widget DOM Container
        removeScriptsFromDom(myScripts);
      }
    }

    const xhrResponseFromCache = getCachedWidget(name, version);

    if (xhrResponseFromCache) {
      xhrFunc(xhrResponseFromCache, true);
      return;
    }

    const xhrReq = new XMLHttpRequest();
    if (!isWidgetViewer && !isLocal()) {
      xhrReq.withCredentials = true;
    }

    const LANG_PATH = (!language || language.toLowerCase() === 'en') ? '' : `${language.toLowerCase()}/`;
    const WIDGET_PATH = `${path}/spa/widgets/${name}/${version}/${LANG_PATH}index.html?_=${Date.now()}`;
    const self = this;

    // execute AJAX request
    xhrReq.open('GET', WIDGET_PATH);

    xhrReq.onreadystatechange = function onreadystatechange() {
      if (this.readyState === 4) {
        if (this.status !== 200) {
          console.error('unable to load widget:', WIDGET_PATH, this.status);

          containers.forEach((dest) => {
            dest.innerHTML = 'unable to load widget';
            self.pub(EVENTS.ERROR_LOADING_WIDGET, {
              widget: {
                name,
                version,
                path,
                container: dest,
              },
              xhrReq: this,
            });
          });
        } else {
          const cacheKey = getCacheKey(name, version);
          xhrResponseCache[cacheKey] = this.responseText;
          xhrFunc(this.responseText, false);
        }
      }
    };

    xhrReq.send();
  }
}

const widgetLoader = new WidgetLoaderClass();
const widgetNamespace = widgetLoader.getNamespace();

window.sparta = window.sparta || {};
window.sparta[widgetNamespace] = window.sparta[widgetNamespace] || {};

if (widgetNamespace && (!(window.sparta && window.sparta[widgetNamespace].widgetLoader))) {
  window.sparta[widgetNamespace].widgetLoader = widgetLoader;
  document.dispatchEvent(new CustomEvent(`${widgetNamespace}.spartaLoaderLoaded`));
}



// WEBPACK FOOTER //
// ./src/index.js